import { ArrowDownRight, ArrowUpRight, Info } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../shared/lib/cn';
import { Tooltip } from '../../../shared/ui';
import { hasDebt } from '../lib/series';
import type { CanvasData } from './use-canvas';

export function Tile({
  label,
  value,
  detail,
  hint,
  tone,
}: {
  label: string;
  value: string;
  detail?: ReactNode;
  /** What the figure means, in a tooltip next to the label. */
  hint?: string;
  tone?: 'negative' | undefined;
}) {
  return (
    <div className="min-w-0 rounded-lg border border-border-subtle bg-bg-1 px-4 py-3">
      <div className="flex min-w-0 items-center gap-1">
        <p className="truncate text-xs text-text-3">{label}</p>
        {hint !== undefined && (
          <Tooltip content={hint}>
            <button
              type="button"
              aria-label={hint}
              className="shrink-0 rounded-sm text-text-3 outline-none hover:text-text-1 focus-visible:text-text-1 focus-visible:ring-2 focus-visible:ring-focus"
            >
              <Info className="size-3" aria-hidden />
            </button>
          </Tooltip>
        )}
      </div>
      <p
        className={cn(
          'tabular mt-1 truncate text-lg font-semibold',
          tone === 'negative' && 'text-negative',
        )}
      >
        {value}
      </p>
      {detail !== undefined && <div className="mt-0.5 truncate text-xs text-text-3">{detail}</div>}
    </div>
  );
}

/** Headline figures for the focused variant, with the difference from the baseline. */
export function KpiStrip({ data }: { data: CanvasData }) {
  const { t } = useTranslation();
  const { project, result, focused, focusedResult, valueMode, dates, fmt } = data;
  if (!focused || !focusedResult) return null;
  const last = result.months - 1;
  const lastDate = dates[last];
  const netWorth = focusedResult.series.netWorth[valueMode];
  const cash = focusedResult.series.cash[valueMode];
  const endValue = netWorth[last] ?? 0;

  let minIndex = 0;
  cash.forEach((v, i) => {
    if (v < (cash[minIndex] ?? 0)) minIndex = i;
  });
  const minCash = cash[minIndex] ?? 0;

  const baseline = project.scenarios.find((s) => s.id === project.baselineScenarioId);
  const baselineResult =
    baseline && baseline.id !== focused.id ? result.scenarios[baseline.id] : undefined;
  const delta = baselineResult
    ? endValue - (baselineResult.series.netWorth[valueMode][last] ?? 0)
    : undefined;

  const mc = data.focusedMc;
  const bands = mc?.bands.netWorth[valueMode];
  const pNegative = mc?.pCashNegativeEver[last] ?? 0;

  const debtFree = focusedResult.metrics.debtFreeMonth;
  const debtFreeDate = debtFree === null ? undefined : dates[debtFree];
  const debtFreeLabel = !hasDebt(focusedResult)
    ? t('kpi.noDebt')
    : debtFreeDate
      ? fmt.monthLong(debtFreeDate)
      : t('kpi.notWithinHorizon');
  const minDate = dates[minIndex];

  return (
    <div
      className="grid grid-cols-2 gap-3 xl:grid-cols-4"
      role="group"
      aria-label={t('kpi.label', { name: focused.name })}
    >
      {bands && data.mc ? (
        <>
          <Tile
            label={t('kpi.medianNetWorthEnd', { year: lastDate ? fmt.year(lastDate) : '' })}
            value={fmt.money(bands.p50[last] ?? 0)}
            tone={(bands.p50[last] ?? 0) < 0 ? 'negative' : undefined}
            detail={t('kpi.p10p90', {
              from: fmt.compact(bands.p10[last] ?? 0),
              to: fmt.compact(bands.p90[last] ?? 0),
            })}
          />
          <Tile
            label={t('kpi.pCashNegative')}
            value={data.fmt.probability(pNegative)}
            tone={pNegative > 0 ? 'negative' : undefined}
            detail={t('kpi.pCashNegativeDetail', {
              paths: data.fmt.count(data.mc.paths),
            })}
          />
        </>
      ) : (
        <>
          <Tile
            label={t('kpi.netWorthEnd', { year: lastDate ? fmt.year(lastDate) : '' })}
            value={fmt.money(endValue)}
            tone={endValue < 0 ? 'negative' : undefined}
            detail={
              delta !== undefined && baseline ? (
                <span className="inline-flex items-center gap-1">
                  {delta >= 0 ? (
                    <ArrowUpRight className="size-3.5 text-positive" aria-hidden />
                  ) : (
                    <ArrowDownRight className="size-3.5 text-negative" aria-hidden />
                  )}
                  {t('kpi.vsBaseline', { delta: fmt.signed(delta), name: baseline.name })}
                </span>
              ) : (
                (data.moneyCaption ?? focused.name)
              )
            }
          />
          <Tile
            label={t('kpi.lowestCash')}
            value={fmt.money(minCash)}
            tone={minCash < 0 ? 'negative' : undefined}
            detail={minDate ? t('kpi.inMonth', { date: fmt.monthLong(minDate) }) : undefined}
          />
        </>
      )}
      <Tile label={t('kpi.debtFree')} value={debtFreeLabel} />
      <Tile
        label={t('kpi.interestPaid')}
        value={fmt.money(focusedResult.metrics.totalInterest)}
        detail={t('kpi.nominal')}
      />
    </div>
  );
}
